<script setup>

import {computed, onMounted, reactive, ref, watch} from "vue";
import {useAdminStore} from "@/store/admin";
import {useToast} from "vue-toast-notification";
import MainPreloader from "@/components/shared/MainPreloader.vue";
import EmptyCard from "@/components/shared/EmptyCard.vue";
import FormInput from "@/components/ui/FormInput.vue";
import CheckBox from "@/components/ui/CheckBox.vue";
import ActionButton from "@/components/ui/ActionButton.vue";
import TablePager from "@/components/shared/TablePager.vue";
import AdminModal from "@/components/AdminView/AdminModal.vue";

const adminStore = useAdminStore();
const toast = useToast();

const PER_PAGE_STORAGE_KEY = 'adminCoinsPerPage';
const currentPage = ref(0);

const PER_PAGE = ref(parseInt(localStorage.getItem(PER_PAGE_STORAGE_KEY)) || 50);

const items = computed(() => adminStore.coins);

const FILTERS_STORAGE_KEY = 'adminCoinsFilters';
const savedFilters = (() => {
    try { return JSON.parse(localStorage.getItem(FILTERS_STORAGE_KEY)) || {}; } catch { return {}; }
})();
const filter = reactive({
    product_id: savedFilters.product_id || "",
    owner: savedFilters.owner || "",
});
watch(filter, () => {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({
        product_id: filter.product_id,
        owner: filter.owner,
    }));
}, {deep: true});

const countPages = computed(() => Math.max(1, Math.ceil((adminStore.totalCoins || 0) / PER_PAGE.value)));
const hasMoreData = computed(() => (adminStore.totalCoins || 0) > (currentPage.value + 1) * PER_PAGE.value);

function buildParams() {
    const params = {page: currentPage.value + 1, per_page: PER_PAGE.value};
    if (filter.product_id) params.product_id = filter.product_id;
    if (filter.owner) params.owner = filter.owner;
    return params;
}

async function loadCoins() {
    await adminStore.loadCoinsData(buildParams());
}

async function setPage(p) {
    if (p < 0 || p >= countPages.value) return;
    currentPage.value = p;
    await loadCoins();
}

function setPerPage(n) {
    const val = parseInt(n);
    if (!val || val < 1) return;
    PER_PAGE.value = val;
    localStorage.setItem(PER_PAGE_STORAGE_KEY, String(val));
    currentPage.value = 0;
    loadCoins();
}

const pagination = {
    loading: computed(() => adminStore.loading),
    currentPage,
    countPages,
    hasMoreData,
    perPage: PER_PAGE,
    setPage,
    setPerPage,
    firstPage: () => setPage(0),
    decPage: () => setPage(currentPage.value - 1),
    incPage: () => setPage(currentPage.value + 1),
    lastPage: () => setPage(countPages.value - 1),
};

function applyFilters() {
    currentPage.value = 0;
    loadCoins();
}

function resetFilters() {
    filter.product_id = "";
    filter.owner = "";
    applyFilters();
}

const editing = ref(null);
const showForm = ref(false);
const form = reactive(defaultForm());

function defaultForm() {
    return {
        product_id: "",
        token: "",
        owner: "",
        number: "",
        phrase: "",
        series: "",
        status: "",
        nft_url: "",
        version: "",
        available: false,
        importance: "",
        user_id: "",
    };
}

function resetForm() {
    Object.assign(form, defaultForm());
}

function openEdit(item) {
    editing.value = item;
    showForm.value = true;
    Object.assign(form, {
        product_id: item.product_id || "",
        token: item.token || "",
        owner: item.owner || "",
        number: item.number || "",
        phrase: item.phrase || "",
        series: item.series || "",
        status: item.status || "",
        nft_url: item.nft_url || "",
        version: item.version || "",
        available: !!item.available,
        importance: item.importance || "",
        user_id: item.user_id || "",
    });
}

function cancelForm() {
    editing.value = null;
    showForm.value = false;
    resetForm();
}

async function saveHandler() {
    const payload = {
        product_id: form.product_id,
        token: form.token,
        owner: form.owner,
        number: form.number,
        phrase: form.phrase,
        series: form.series,
        status: form.status,
        nft_url: form.nft_url,
        version: form.version,
        available: form.available,
        importance: form.importance,
        user_id: form.user_id,
    };
    const isEdit = !!editing.value?.id;
    const ok = isEdit
        ? await adminStore.updateItem('coins', editing.value.id, payload)
        : await adminStore.createItem('coins', payload);
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    if (ok) {
        toast.success(isEdit ? "Монета обновлена" : "Монета создана");
        cancelForm();
        await loadCoins();
    }
}

async function deleteHandler(item) {
    if (!confirm(`Удалить монету (number: "${item.number || item.id}")?`)) return;
    const ok = await adminStore.deleteItem('coins', item.id);
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    if (ok) {
        toast.success("Монета удалена");
        await loadCoins();
    }
}

onMounted(() => {
    loadCoins();
});

</script>

<template>
  <div class="adminCrud">
    <MainPreloader
        v-if="adminStore.loading"
        position="fixed"
        :size="80"
        :stroke="3"
    />

    <div class="adminCrud__toolbar">
      <div class="adminCrud__toolbarTitle">Монеты</div>
    </div>

    <AdminModal :visible="showForm" :title="editing?.id ? 'Редактирование монеты' : 'Новая монета'" @close="cancelForm">
      <div class="adminCrud__formGrid">
        <div class="adminCrud__field">
          <label class="adminCrud__label">ID продукта</label>
          <form-input v-model="form.product_id"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Токен</label>
          <form-input v-model="form.token"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Владелец</label>
          <form-input v-model="form.owner"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Номер</label>
          <form-input v-model="form.number"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Серия</label>
          <form-input v-model="form.series"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Статус</label>
          <form-input v-model="form.status"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Версия</label>
          <form-input v-model="form.version"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Важность</label>
          <form-input v-model="form.importance"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">ID пользователя</label>
          <form-input v-model="form.user_id"/>
        </div>
        <div class="adminCrud__field adminCrud__field--wide">
          <label class="adminCrud__label">Фраза</label>
          <form-input v-model="form.phrase" rows="3"/>
        </div>
        <div class="adminCrud__field adminCrud__field--wide">
          <label class="adminCrud__label">NFT URL</label>
          <form-input v-model="form.nft_url" rows="3"/>
        </div>
      </div>
      <div class="adminCrud__checks">
        <check-box v-model="form.available" text="Доступна"/>
      </div>
      <template #footer>
        <ActionButton @buttonClick="cancelForm">
          <template #caption>Отмена</template>
        </ActionButton>
        <ActionButton style="background-color: #6FC062; color: #212226;" @buttonClick="saveHandler">
          <template #caption>Сохранить</template>
        </ActionButton>
      </template>
    </AdminModal>

    <div class="adminCrud__filters">
      <div class="adminCrud__filterRow">
        <div class="adminCrud__filterField">
          <label class="adminCrud__label">ID продукта</label>
          <form-input v-model="filter.product_id" @input="applyFilters"/>
        </div>
        <div class="adminCrud__filterField">
          <label class="adminCrud__label">Владелец</label>
          <form-input v-model="filter.owner" @input="applyFilters"/>
        </div>
        <div class="adminCrud__filterActions">
          <ActionButton @buttonClick="resetFilters">
            <template #caption>Сбросить</template>
          </ActionButton>
        </div>
      </div>
    </div>

    <TablePager
        v-if="items.length && pagination.countPages.value >= 1"
        :pagination="pagination"
    />

    <EmptyCard v-if="!items.length && !adminStore.loading"/>

    <table v-if="items.length" class="adminTable">
      <thead>
      <tr>
        <th>Номер</th>
        <th>Серия</th>
        <th>Статус</th>
        <th>Действия</th>
        <th>Владелец</th>
        <th>Доступна</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="item in items" :key="item.id">
        <td>{{ item.number || '—' }}</td>
        <td>{{ item.series || '—' }}</td>
        <td>{{ item.status || '—' }}</td>
        <td class="adminCrud__actions">
          <button class="adminCrud__actionButton" title="Изменить" @click="openEdit(item)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button class="adminCrud__actionButton adminCrud__actionButton--danger" title="Удалить" @click="deleteHandler(item)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6h18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10 11v6M14 11v6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </td>
        <td>{{ item.owner || '—' }}</td>
        <td>{{ item.available ? 'Да' : 'Нет' }}</td>
      </tr>
      </tbody>
    </table>

    <TablePager
        v-if="pagination.countPages.value >= 1"
        :pagination="pagination"
    />
  </div>
</template>

<style scoped lang="scss">
.adminCrud {
  display: flex;
  flex-direction: column;
  gap: 14px;

  &__toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__toolbarTitle {
    font-size: 20px;
    font-weight: 600;
    color: var(--cd-text);
  }

  &__form {
    background: var(--cd-card-bg);
    border-radius: 20px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    text-align: left;
  }

  &__formTitle {
    color: var(--cd-base-green);
    font-size: 16px;
    font-family: var(--cd-default-font);
  }

  &__formGrid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @include respond-to('md') {
      grid-template-columns: 1fr;
    }
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &--wide {
      grid-column: 1 / -1;
    }
  }

  &__label {
    color: var(--cd-gray-alt);
    font-size: 13px;
  }

  &__checks {
    display: flex;
    gap: 24px;
  }

  &__filters {
    background: var(--cd-card-bg);
    border-radius: 20px;
    padding: 20px;
  }

  &__filterRow {
    display: flex;
    gap: 16px;
    align-items: flex-end;
    flex-wrap: wrap;

    @include respond-to('md') {
      flex-direction: column;
      align-items: stretch;
    }
  }

  &__filterField {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 180px;
  }

  &__filterActions {
    display: flex;
    gap: 8px;
    align-items: flex-end;
  }

  &__formActions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
  }

  &__actions {
    vertical-align: middle;
    white-space: nowrap;
    font-size: 0;

    .adminCrud__actionButton + .adminCrud__actionButton {
      margin-left: 8px;
    }
  }

  &__actionButton {
    background: transparent;
    border: 1px solid var(--cd-base-green);
    color: var(--cd-base-green);
    border-radius: 8px;
    padding: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;

    &:hover {
      background: var(--cd-base-green);
      color: #ffffff;
    }

    &--danger {
      border-color: var(--cd-danger);
      color: var(--cd-danger);

      &:hover {
        background: var(--cd-danger);
        color: #ffffff;
      }
    }
  }
}

.adminTable {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 8px;
  text-align: left;

  @include respond-to('md') {
    display: block;
    overflow-x: auto;
  }

  th {
    color: var(--cd-text-muted);
    font-size: 12px;
    font-weight: 400;
    padding: 0 16px 4px;
  }

  td {
    background: var(--cd-card-bg);
    padding: 12px 16px;
    font-size: 14px;
    color: var(--cd-text);

    &:first-child {
      border-radius: 12px 0 0 12px;
    }

    &:last-child {
      border-radius: 0 12px 12px 0;
    }
  }
}
</style>
